import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'

/**
 * Inline-редактирование числа в ячейке таблицы.
 *
 * Поведение:
 *   - Ввод свободный (можно ввести «0», «», запятую/точку).
 *   - Коммит: blur или Enter, если значение валидно и отличается от исходного.
 *   - Откат: Escape сбрасывает локальное значение к `value`.
 *   - При обновлении внешнего `value` локальный буфер пересинхронизируется,
 *     если поле не в фокусе.
 */
export function InlineNumberInput({
  value,
  onCommit,
  disabled,
  busy,
  step = 1,
  min,
  allowNullable,
  placeholder,
  width = 96,
  ariaLabel,
  invalid,
  formatter = (n) => String(n),
  parser = (s) => Number(s.replace(',', '.')),
}: {
  value: number | null
  onCommit: (next: number | null) => void | Promise<void>
  disabled?: boolean
  busy?: boolean
  step?: number
  min?: number
  /** Разрешать пустую строку как `null` (для `range_to = ∞`). */
  allowNullable?: boolean
  placeholder?: string
  width?: number
  ariaLabel?: string
  invalid?: boolean
  formatter?: (n: number) => string
  parser?: (s: string) => number
}) {
  const { t } = useTranslation()
  const [draft, setDraft] = useState<string>(value == null ? '' : formatter(value))
  const [localError, setLocalError] = useState<string | null>(null)
  const ref = useRef<HTMLInputElement>(null)
  const focused = useRef(false)

  useEffect(() => {
    if (focused.current) return
    setDraft(value == null ? '' : formatter(value))
  }, [value, formatter])

  function tryCommit() {
    const trimmed = draft.trim()
    if (trimmed === '') {
      if (allowNullable) {
        if (value === null) return
        setLocalError(null)
        void onCommit(null)
        return
      }
      setLocalError(t('validate.enterNumber'))
      return
    }
    const parsed = parser(trimmed)
    if (!Number.isFinite(parsed)) {
      setLocalError(t('validate.notNumber'))
      return
    }
    if (min != null && parsed < min) {
      setLocalError(`>= ${min}`)
      return
    }
    if (parsed === value) return
    setLocalError(null)
    void onCommit(parsed)
  }

  return (
    <div className="inline-flex flex-col">
      <input
        ref={ref}
        type="text"
        inputMode="decimal"
        disabled={disabled || busy}
        aria-label={ariaLabel}
        aria-invalid={invalid || localError != null || undefined}
        placeholder={placeholder}
        value={draft}
        style={{ width }}
        onFocus={() => {
          focused.current = true
        }}
        onBlur={() => {
          focused.current = false
          tryCommit()
        }}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            ref.current?.blur()
          } else if (e.key === 'Escape') {
            setDraft(value == null ? '' : formatter(value))
            setLocalError(null)
            ref.current?.blur()
          }
        }}
        className={clsx(
          'stryzh-input-field',
          (invalid || localError) && 'stryzh-input-field--error',
        )}
        step={step}
      />
      {localError ? (
        <span className="stryzh-input-error-text mt-1 text-[length:var(--stryzh-font-size-xs)]">
          {localError}
        </span>
      ) : null}
    </div>
  )
}
